import { uploadDocumentWithTray } from "actions/documents";
import classNames from "classnames";
import { Icon } from "components/Documents";
import { TextInput } from "components/Inputs";
import { Button, ListGroup, ListGroupItem, LoadingState, ModalHeader } from "components/Shared";
import { whatIs } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import { setMergedStateWrapper } from "lib/hooks";
import _ from "lodash";
import moment from "moment";
import React, { Dispatch, memo, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { useDispatch } from "react-redux";
import { toSentenceSerial } from "underscore.string";
import { CommentContext } from "./CardDynamic";

// ###### Comment Modal ############################################################################################################################################################

const CommentModal = () => {
	// The modal now reads and writes the SHARED draftComment from CommentContext
	// rather than maintaining an isolated local copy (F3). This means:
	//  - When the modal opens the text already in context (typed in the main box)
	//    is immediately available (criterion a).
	//  - When the user types in the modal it writes back to context, so closing
	//    without sending preserves those edits in the main box (criterion b).
	const { context: commentCtx, setContext: setCommentCtx } = useContext(CommentContext);
	const setMergedCommentCtx = setMergedStateWrapper(setCommentCtx);

	const { context, setContext } = useContext(CommentDocumentContext);
	const setMergedContext = setMergedStateWrapper(setContext);
	const { globalId, uuid, users, files, commentModalOpen: modalOpen } = context;

	// Derive comment text from the shared context (F3).
	const draftComment: string = commentCtx.draftComment ?? "";
	const draftMetadata: any = commentCtx.draftMetadata ?? {};

	const setComment = (html: string) => setMergedCommentCtx({ draftComment: html });

	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "comment", renderTrigger: renderTrigger }));
	const dispatch = useDispatch();

	// closeModal intentionally does NOT clear draftComment — the draft must
	// survive a close-without-send so it flows back into the main box (F5,
	// criterion b). The main box handles re-display via contentKey bump via
	// the onClose callback stored in CommentDocumentContext (wired in
	// CreateWithDocument so every close path — X button, backdrop,
	// Ok-without-send — triggers the main editor re-hydration).
	const closeModal = useCallback(() => {
		setMergedContext({ commentModalOpen: false, uploadModalOpen: false });
		if (context.onClose) context.onClose();
	}, [setMergedContext, context]);

	const submitComment = () => {
		datasource.setIsLoading(true);
		_.forEach(files, file => {
			// Upload first (see actions/documents#createDocumentUploadFirst): the
			// Document — and so the comment that links to it — only exists once
			// the file is in S3. A failed or cancelled upload creates neither.
			uploadDocumentWithTray(`${whatIs(globalId)}/${uuid}`, file, {}, dispatch, (document) => {
				if (draftComment !== "") {
					// Merge document link metadata with any existing draft metadata (F3).
					// reply_to_id is included from CommentContext to support reply-mode
					// document comments (API contract + acceptance criterion reply edge case).
					datasource.post(
						"v2",
						"comments",
						{
							body: {
								global_id: globalId,
								body: draftComment,
								reply_to_id: _.get(commentCtx, "replyToComment.id", null),
								metadata: {
									...draftMetadata,
									ogSiteName: "tcpinpoint",
									ogTitle: document.name,
									ogUrl: `${process.env.REACT_APP_HOST}documents/${document.id}`,
									ogDescription: ""
								}
							}
						},
						{ updateIsLoding: true }
					);
				}
			}).then((document) => {
				// Nothing was created — let the modal go back to the editor so the
				// draft can be resent, rather than spinning forever.
				if (!document) datasource.setIsLoading(false);
			});
		});
	};

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success && context.onSubmitSuccess) {
				closeModal();
				// Clear the shared draft after a successful document-comment send
				// (criterion d — document-upload send path).
				setMergedCommentCtx({ draftComment: "", draftMetadata: {} });
				context.onSubmitSuccess(response.normalizedMainModelResponse);
			}
		});
		return () => sub.unsubscribe();
	}, [datasource, closeModal, context, setMergedCommentCtx]);

	return (
		<Modal
			ariaHideApp={false}
			className="document-upload-modal"
			isOpen={modalOpen}
			onRequestClose={closeModal}
			contentLabel="zip"
		>
			<ModalHeader title="upload" onRequestClose={closeModal} />
			<section>
				{!!users.length && (
					<div className="alert alert-info">
						<i className="fas fa-exclamation-circle" /> Shared with {toSentenceSerial(_.map(users, "name"))}
					</div>
				)}
				{datasource.isLoading ? (
					<LoadingState />
				) : (
					// The modal editor mounts/remounts each time commentModalOpen toggles
					// true because CreateWithDocument renders it only when the modal is
					// open. On mount DraftText hydrates from `body` (= draftComment)
					// so the main-box draft pre-populates the modal editor (criterion a).
					// contentKey is set to the stringified open-state so that if the user
					// opens, closes, and re-opens we get a fresh hydration each time.
					<TextInput
						users={users}
						body={draftComment}
						contentKey={`modal-${modalOpen}`}
						name="body"
						onChange={setComment}
						placeholder="Add a comment about the file"
						spellCheck={true}
					/>
				)}
				<br />
				<ListGroup
					collection={files}
					pagination={false}
					resource="documents"
					renderRow={(document: any) => (
						<ListGroupItem
							key={document.name}
							heading={document.name}
							icon={<Icon document={{} as tcpinpoint.Document} />}
						/>
					)}
				/>
			</section>
			<footer>{!datasource.isLoading && <Button onClick={submitComment} />}</footer>
		</Modal>
	);
};

// ###### Upload Modal ############################################################################################################################################################

const UploadModal = () => {
	const uploadRef = useRef<HTMLInputElement>(null);
	const divRef = useRef(null);
	const { context, setContext } = useContext(CommentDocumentContext);
	const { uuid } = context;
	const setMergedContext = setMergedStateWrapper(setContext);

	// upload documents from local to browser, not actual upload to AWS cloud
	const onUploadDocuments = () => {
		const { files } = uploadRef.current as HTMLInputElement;
		setMergedContext({ files: files || [], commentModalOpen: true });
	};

	const onDrop = (event: any) => {
		event.preventDefault();
		const { files } = event.dataTransfer;
		if(files.length > 1){
			return;
		}
		setMergedContext({ files: files || [], commentModalOpen: true });
	};

	const onDragOver = (event: any) => {
		if (divRef && divRef.current) {
			//@ts-ignore
			divRef.current.style.borderColor = "red";
			event.preventDefault();
		}
	};

	return (
		<div ref={divRef} onDrop={onDrop} onDragOver={onDragOver} className="upload">
			<input
				className="file-input"
				ref={uploadRef}
				onChange={onUploadDocuments}
				// Reset the value on each click so selecting the same file again still
				// fires onChange (browsers skip the event when the value is unchanged),
				// which is what reopens the comment modal after it has been closed.
				onClick={(event) => {
					(event.target as HTMLInputElement).value = "";
				}}
				id={`${uuid}-file`}
				name={`${uuid}-file`}
				type="file"
			/>
			<label htmlFor={`${uuid}-file`} className="btn btn-primary btn-sm button">
				<i className="fas fa-fw fa-file-upload" /> Add a file from your computer (One document per comment)
			</label>
		</div>
	);
};

interface ICommentDocumentContext {
	context: any;
	setContext: Dispatch<SetStateAction<any>>;
}
export const CommentDocumentContext = React.createContext({
	context: {} as any,
	setContext: (context: any) => {}
} as ICommentDocumentContext);

// ###### CreateWithDocument ############################################################################################################################################################

// `comment` prop removed — the modal reads from CommentContext directly (F3).
// `onClose` prop added so CreateDynamic can receive the close signal and bump
// mainEditorContentKey, re-hydrating the main editor from CommentContext.draftComment
// (criterion b fix).
const CreateWithDocument = ({ globalId, isOpen: isUploadModalOpen, onClose, onSubmitSuccess, users }: any) => {
	const ref = useRef(null);
	const [context, setContext] = useState({
		uploadModalOpen: isUploadModalOpen,
		commentModalOpen: false,
		// `comment` field intentionally omitted — draft lives in CommentContext (F3).
		files: [] as any[],
		users: users,
		globalId: globalId, // the global id of commentable object, such as project / task etc.
		uuid: globalId.match(/[a-zA-Z0-9-]*?$/), // the database uuid of commentable object, such as project / task etc.
		onSubmitSuccess: onSubmitSuccess,
		// Store onClose in context so CommentModal.closeModal can invoke it on
		// every close path (X button, backdrop, Ok-without-send).  Criterion b.
		onClose: onClose
	} as any);
	const commentDocumentContextProviderValue = { context, setContext };
	const triggerUploadModal = () =>
		setContext((prevState: any) => ({ ...prevState, uploadModalOpen: !prevState.uploadModalOpen }));

	useEffect(() => setContext((prevState: any) => ({ ...prevState, users, uploadModalOpen: isUploadModalOpen, onClose })), [
		isUploadModalOpen,
		onClose,
		users,
		setContext
	]);

	return (
		<CommentDocumentContext.Provider value={commentDocumentContextProviderValue}>
			<React.Fragment>
				<button
					className={classNames("button", "btn", "btn-add", "btn-primary", {
						"action-menu-open": context.uploadModalOpen
					})}
					id="action-menu-button"
					type="button"
					onClick={triggerUploadModal}
				>
					<i className="fas fa-plus" />
				</button>
				<ul ref={ref} className={classNames({ "action-menu-open": context.uploadModalOpen })} id="action-menu">
					<li>
						<UploadModal />
						<CommentModal />
					</li>
				</ul>
			</React.Fragment>
		</CommentDocumentContext.Provider>
	);
};

export default memo(CreateWithDocument);
